/**
 * contrast.ts — WCAG 2.1 contrast-ratio + verdict helpers.
 *
 * Thresholds (CONTEXT D-12):
 *   BLOCK: ratio < 3.0      — fails even the 3:1 large-text minimum
 *   WARN:  3.0 ≤ ratio <4.5  — ok for ≥18pt / ≥14pt-bold large text only
 *   AA:    4.5 ≤ ratio <7.0  — WCAG 2.1 AA body text
 *   AAA:   ratio ≥ 7.0       — WCAG 2.1 AAA body text
 *
 * Tenant-primary derivation flags WARN (3.0..4.5) with a hint: buttons using
 * `--color-text-on-primary` remain correct (white-on-primary meets contrast),
 * but body text on a `bg-primary` surface fails AA.
 */
import { wcagContrast } from "culori";

export type WcagVerdict = "BLOCK" | "WARN" | "AA" | "AAA";

export const WCAG_THRESHOLDS = {
  BLOCK_MAX: 3.0,
  WARN_MAX: 4.5,
  AAA_MIN: 7.0,
} as const;

/**
 * Returns WCAG 2.1 contrast ratio between foreground and background.
 * Falls back to `1` (worst-case identical-colors) on invalid input so callers
 * never see NaN / undefined propagating into downstream math.
 */
export function contrastRatio(fg: string, bg: string): number {
  const r = wcagContrast(fg, bg);
  return r ?? 1;
}

/** Maps a raw contrast ratio to the four-level verdict used by validateTenantPrimary. */
export function wcagVerdict(ratio: number): WcagVerdict {
  if (ratio < WCAG_THRESHOLDS.BLOCK_MAX) return "BLOCK";
  if (ratio < WCAG_THRESHOLDS.WARN_MAX) return "WARN";
  if (ratio < WCAG_THRESHOLDS.AAA_MIN) return "AA";
  return "AAA";
}
